import React from 'react';
import { CheckCircle, XCircle, AlertCircle, ExternalLink } from 'lucide-react';

const ActionExecutionResults = ({ executionResults, errors = [] }) => {
  if (!executionResults || executionResults.length === 0) {
    return null;
  }

  const successfulResults = executionResults.filter(r => r.success);
  const failedResults = executionResults.filter(r => !r.success);

  return (
    <div className="action-execution-results bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-3">
      <div className="flex items-center space-x-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Execution Results
        </h3>
        {successfulResults.length > 0 && (
          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
            {successfulResults.length} succeeded
          </span>
        )}
        {failedResults.length > 0 && (
          <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded">
            {failedResults.length} failed
          </span>
        )}
      </div>

      <div className="space-y-2">
        {successfulResults.map((result, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-800 rounded p-3"
          >
            <div className="flex items-start space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {result.name === 'create_entity' ? 'Entity Created' : result.name}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  {result.message}
                </div>
                {result.result && (
                  <div className="mt-2 space-y-1">
                    {result.result.entityId && (
                      <div className="text-xs text-gray-700 dark:text-gray-300">
                        <strong>Entity ID:</strong> {result.result.entityId}
                      </div>
                    )}
                    {result.result.entityType && (
                      <div className="text-xs text-gray-700 dark:text-gray-300">
                        <strong>Type:</strong> {result.result.entityType}
                      </div>
                    )}
                    {result.result.entity && result.result.entityId && (
                      <div className="text-xs">
                        <a
                          href={`/${result.result.entityType?.toLowerCase() || 'entity'}/${result.result.entityId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
                        >
                          <span>View Record</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </div>
                )}
                {result.duration && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Completed in {result.duration}ms
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {failedResults.map((result, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded p-3"
          >
            <div className="flex items-start space-x-2">
              <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {result.name} - Failed
                </div>
                <div className="text-xs text-red-600 dark:text-red-400">
                  {result.error || result.message || 'Unknown error'}
                </div>
              </div>
            </div>
          </div>
        ))}

        {errors.map((error, index) => (
          <div
            key={`error-${index}`}
            className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded p-3"
          >
            <div className="flex items-start space-x-2">
              <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {error.name} - Error
                </div>
                <div className="text-xs text-red-600 dark:text-red-400">
                  {error.error || 'Unknown error'}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActionExecutionResults;

